import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, Check } from "lucide-react";
import { Btn } from "./Reveal";
import { INTERESTS } from "@/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full bg-white border border-[#D4D4D4] px-4 py-3.5 text-[15px] text-[var(--ink)] placeholder:text-neutral-500 focus:outline-none focus:border-[var(--ink)] transition-colors duration-200";

const Input = ({ name, form, set, placeholder, type = "text", required = true, className = "" }) => (
  <input name={name} type={type} required={required} value={form[name]} onChange={set} placeholder={placeholder} aria-label={placeholder} data-testid={`lead-${name}-input`} className={`${inputCls} ${className}`} />
);

const Interests = ({ value, toggle, twoCol }) => (
  <fieldset data-testid="lead-interests">
    <legend className="font-head font-semibold text-[14px] mb-3">What are you {twoCol ? "interested in? *" : "most interested in?"}</legend>
    <div className={`grid gap-y-2.5 gap-x-6 ${twoCol ? "sm:grid-cols-2" : ""}`}>
      {INTERESTS.map((o) => {
        const on = value.includes(o);
        return (
          <button type="button" key={o} onClick={() => toggle(o)} data-testid={`interest-${o.toLowerCase().replace(/\s+/g, "-")}`} aria-pressed={on} className="flex items-center gap-3 text-left text-[14px] text-[var(--body)]">
            <span className={`h-[18px] w-[18px] grid place-items-center border transition-colors ${on ? "bg-[var(--ink)] border-[var(--ink)]" : "border-[#BDBDBD] bg-white"}`}>{on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}</span>
            {o}
          </button>
        );
      })}
    </div>
  </fieldset>
);

export default function LeadForm({ kind, initialInterest, date, time, onDone }) {
  const consult = kind === "consultation";
  const empty = { first_name: "", last_name: "", phone: "", email: "", zip_code: "", address: "", city: "", heard_about: "", notes: "", interests: initialInterest ? [initialInterest] : [] };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const toggle = (o) => setForm((f) => ({ ...f, interests: f.interests.includes(o) ? f.interests.filter((x) => x !== o) : [...f.interests, o] }));

  const submit = async (e) => {
    e.preventDefault();
    if (consult && form.interests.length === 0) {
      toast.error("Please pick at least one interest.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...form, kind, preferred_date: date || "", preferred_time: time || "" });
      setDone(true);
      setForm(empty);
      onDone?.();
      toast.success("Request received. We'll be in touch soon.");
    } catch (err) {
      toast.error(err?.response?.status === 422 ? "Please check your details and try again." : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-success" className="py-10 flex flex-col">
        <CheckCircle2 className="h-12 w-12" strokeWidth={1.4} />
        <p className="headline mt-6 text-3xl">Thanks — request received.</p>
        <p className="mt-3 text-[16px] text-[var(--body)]">A member of our team will reach out within 1 business day to confirm your appointment.</p>
        <Btn variant="outline" onClick={() => setDone(false)} testId="lead-new-request-btn" className="mt-8 self-start">Send another</Btn>
      </div>
    );
  }

  return (
    <form data-testid={`${kind}-form`} onSubmit={submit} className="grid grid-cols-2 gap-3">
      <Input name="first_name" form={form} set={set} placeholder={`First Name${consult ? " *" : ""}`} />
      <Input name="last_name" form={form} set={set} placeholder={`Last Name${consult ? " *" : ""}`} />
      <Input name="phone" type="tel" form={form} set={set} placeholder={`Phone Number${consult ? " *" : ""}`} className={consult ? "" : "col-span-2"} />
      <Input name="email" type="email" form={form} set={set} placeholder={`Email Address${consult ? " *" : ""}`} className={consult ? "" : "col-span-2"} />
      {consult && <Input name="address" form={form} set={set} placeholder="Address *" className="col-span-2" />}
      {consult ? (
        <div className="col-span-2 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Input name="city" form={form} set={set} placeholder="City *" />
          <Input name="zip_code" form={form} set={set} placeholder="ZIP Code *" />
          <select name="heard_about" value={form.heard_about} onChange={set} data-testid="lead-heard_about-select" aria-label="How did you hear about us?" className={`${inputCls} col-span-2 sm:col-span-1 cursor-pointer text-[14px]`}>
            <option value="">How did you hear about us?</option>
            {["Google", "Social media", "Friend or neighbor", "Saw our team", "Other"].map((o) => <option key={o}>{o}</option>)}
          </select>
        </div>
      ) : (
        <Input name="zip_code" form={form} set={set} placeholder="ZIP Code" className="col-span-2" />
      )}
      <div className="col-span-2 mt-4"><Interests value={form.interests} toggle={toggle} twoCol={consult} /></div>
      {consult && (
        <label className="col-span-2 mt-4 block">
          <span className="font-head font-semibold text-[14px]">Additional Questions or Comments</span>
          <textarea name="notes" rows={3} value={form.notes} onChange={set} data-testid="lead-notes-input" placeholder="Tell us more about your home, water concerns, or any questions you have..." className={`${inputCls} mt-3 resize-none`} />
        </label>
      )}
      {consult && (date || time) && (
        <p data-testid="lead-selected-slot" className="col-span-2 text-[14px] text-[var(--body)]">Preferred time: <span className="font-semibold text-[var(--ink)]">{[date, time].filter(Boolean).join(" at ")}</span></p>
      )}
      <Btn type="submit" disabled={loading} chevron={!loading} testId="lead-submit-btn" className="col-span-2 mt-4 w-full">
        {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : consult ? "Request My Consultation" : "Request My Free Assessment"}
      </Btn>
      <p className="col-span-2 text-[12px] text-[var(--body)]">{consult ? "A member of our team will contact you within 1 business day to confirm your appointment." : "A member of our team will reach out to schedule a convenient time for your in-home water test."}</p>
    </form>
  );
}
